import {BasicInterval, Metric, parseUTCInterval, Site, UnitSystem} from "../../common";
import {DataPoint, DataService, MetricDataRequestOptions} from "../../data";
import {Interval} from "luxon";

export interface MapData {
    sites: Site[];
    values: Map<Site, number>;
    metric: Metric;
}

export interface DataProvider {
    getData({sites, metric, interval}: {
        sites: Site[] | Set<Site>,
        metric: Metric | string,
        interval: BasicInterval | Interval
    }): MapData | Promise<MapData>;
}

export type DataRequestTransformer = (request: MetricDataRequestOptions) => MetricDataRequestOptions;
export type DataAggregator = (dataPoints: DataPoint[]) => number;
export type DataFilter = (dataPoints: DataPoint[]) => DataPoint[];

export class ApiDataProvider implements DataProvider {

    private readonly data: DataService;
    private readonly filter: DataFilter;
    private readonly requestTransformer: DataRequestTransformer;
    private readonly aggregator: DataAggregator;

    public constructor({data, aggregator, filter, requestTransformer}: {
        data: DataService,
        aggregator?: DataAggregator,
        filter?: DataFilter,
        requestTransformer?: DataRequestTransformer
    }) {
        this.data = data;
        this.aggregator = aggregator || (ds => ds[0]?.value);
        this.filter = filter || (ds => ds);
        this.requestTransformer = requestTransformer || (r => r);
    }

    public async getData({sites, metric, interval}: {
        sites: Site[] | Set<Site>,
        metric: Metric | string,
        interval: BasicInterval | Interval
    }): Promise<MapData> {
        const siteData = new Map<Site, number>();
        const siteArray = Array.isArray(sites) ? sites : [...sites];
        const siteSet = Array.isArray(sites) ? new Set(sites) : sites;

        const request: MetricDataRequestOptions = this.requestTransformer({
            sites: siteSet,
            metric: this.data.getMetric(metric),
            interval: parseUTCInterval(interval)
        });

        (await this.data.getMetricData(request))
            .forEach((dataSet, site) => {
                const value =this.aggregator(
                    this.filter(
                        dataSet.getData(UnitSystem.METRIC, request.interval)
                    )
                );
                if (typeof value === 'number') {
                    siteData.set(site, value);
                }
            });

        return {sites: siteArray, values: siteData, metric: request.metric};
    }

}